import { useQueryClient } from "@tanstack/react-query";
import { translate } from "../i18n/i18nConfig";
import {
  GetAllCheckListsResponse,
  IChecklistParams,
  IChecklistQuestionType,
  ICheckLists
} from "../interfaces/CheckLists";
import { showGlobalSnackbar } from "../providers/SnackbarProvider";
import { useAppMutation } from "./useAppMutation";
import { useAppQuery } from "./useAppQuery";

export const useGetCheckListType = (enabled: boolean) => {
  const { data } = useAppQuery<{ questionTypes: IChecklistQuestionType[] }>(
    "/Checklist/questiontypes",
    {
      enabled: enabled
    }
  );
  return { listQuestionTypes: data };
};

export const useCreateCheckListMutation = (onClose: () => void, resetForm: () => void) => {
  const queryClient = useQueryClient();
  const { mutate } = useAppMutation("post", "/Checklist", {
    onSuccess: () => {
      showGlobalSnackbar(translate("messages.checklistCreatedSuccess"), "success");
      onClose();
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["/Checklist"] });
    },
    onError: (error) => {
      showGlobalSnackbar((error as any).response.data.errorMessage, "error");
    }
  });
  return { createCheckList: mutate };
};

export const useGetAllCheckLists = (params: IChecklistParams) => {
  const { data, isLoading } = useAppQuery<GetAllCheckListsResponse>("/Checklist", {
    config: {
      params: params
    }
  });
  return { checkLists: data?.checklists, isLoading };
};

export const useDeleteCheckListMutation = (checklistId: string, onDeleteSelected: () => void) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useAppMutation("delete", `/Checklist/${checklistId}`, {
    onSuccess: () => {
      showGlobalSnackbar(translate("messages.checklistDeletedSuccess"), "success");
      queryClient.invalidateQueries({ queryKey: ["/Checklist"] });
      onDeleteSelected();
    },
    onError: (error) => {
      showGlobalSnackbar((error as any).response.data.errorMessage, "error");
    }
  });
  return { deleteCheckList: mutate, isPending };
};

export const useGetCheckListById = (checklistId: string, isOpen?: boolean) => {
  const { data, isLoading } = useAppQuery<ICheckLists>(`/Checklist/${checklistId}`, {
    enabled: !!checklistId && isOpen,
    placeholderData: undefined
  });
  return { checklist: data, isLoading };
};

export const useUpdateCheckListMutation = (onClose: () => void) => {
  const { mutate } = useAppMutation("put", "/Checklist", {
    onSuccess: () => {
      showGlobalSnackbar(translate("messages.checklistUpdatedSuccess"), "success");
      onClose();
    },
    onError: (error) => {
      showGlobalSnackbar((error as any).response.data.errorMessage, "error");
    }
  });
  return { updateCheckList: mutate };
};
